import { computed, inject, Service } from '@angular/core';
import { lastValueFrom, Observable, switchMap, timer } from 'rxjs';
import { LiveParticipant, ParticipantToValidate, Live, UserInformation } from '@shared/models/live';
import { InboundWsMessage } from '@shared/models/websocket';
import { getShortName } from '@shared/utils/user';
import { WebSocketActionEnum } from '@shared/enums/websocket';
import { RoleEnum } from '@shared/enums/user';
import { InvitedPeopleDto, InvitePeopleDto } from '@shared/models/calendar';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { LiveCommand } from '../realtime/live-command';
import { SyncState, syncState } from '@shared/utils/sync-state';

type MuteAction = WebSocketActionEnum.MUTE | WebSocketActionEnum.UNMUTE | WebSocketActionEnum.MUTE_SELF | WebSocketActionEnum.UNMUTE_SELF;

@Service()
export class LiveParticipants {
  private readonly liveCommands = inject(LiveCommand);
  private readonly httpClient = inject(HttpClient);

  private readonly participants = syncState<LiveParticipant[]>([]);
  public readonly participantList = this.participants.value;
  public readonly participantList$ = this.participants.changes$;
  private readonly currentParticipant = syncState<LiveParticipant | null>(null);
  public readonly currentLiveParticipant = this.currentParticipant.value;
  public readonly currentLiveParticipant$ = this.currentParticipant.changes$;
  private readonly toValidate = syncState<ParticipantToValidate[]>([]);
  public readonly participantsToValidate = this.toValidate.value;
  public readonly participantsToValidate$ = this.toValidate.changes$;
  private readonly toValidateNotification = syncState<ParticipantToValidate[]>([]);
  public readonly participantsToValidateNotification = this.toValidateNotification.value;
  public readonly participantsToValidateNotification$ = this.toValidateNotification.changes$;

  public readonly numberOfConnectedParticipants = computed(
    () =>
      this.participantList().filter(
        (u) =>
          u.uuid !== 'Mixer_record' &&
          !u.isAudioOnly &&
          ((u.participant?.status as string) === 'Connecting' ||
            (u.participant?.status as string) === 'Connected' ||
            (u.participant?.status as string) === 'Inactive'),
      ).length,
  );

  setParticipants(participants: LiveParticipant[]): void {
    this.participants.set(participants);
  }

  setCurrentLiveParticipant(participant: LiveParticipant | null): void {
    this.currentParticipant.set(participant);
  }

  setParticipantsToValidate(participants: ParticipantToValidate[]): void {
    this.toValidate.set(participants);
  }

  public refreshParticipantList(live: Live) {
    this.participants.set(
      this.participants.get().map((p) => ({
        ...p,
        liveRole: this.getUserInformation(p.uuid, live)?.role,
        appRights: this.getAppUserRights(p.uuid, live),
      })),
    );
  }

  public getAppUserRights(uuid: string | undefined, live: Live): string[] {
    if (uuid) {
      const permission = live.liveRights?.[uuid]?.linkPermissions;
      return permission ?? [];
    }
    return [];
  }

  public getUserInformation(uuid: string | undefined, live: Live): UserInformation | null {
    return live.usersInformations?.find((u) => u.email.toLowerCase() === uuid?.toLowerCase()) ?? null;
  }

  filterAndInviteRemoteExperts(participants: ParticipantToValidate[], currentLive: Live | null) {
    if (currentLive != null) {
      const userInfos = participants.map((participant) => this.getUserInformation(participant.email, currentLive));

      const remoteExperts = userInfos.filter((userInfo) => userInfo?.role === RoleEnum.Moderator || userInfo?.role === RoleEnum.Owner);

      this.toValidate.set(participants);

      remoteExperts.forEach((remoteExpert) => {
        if (remoteExpert && currentLive != null) {
          void this.inviteParticipant(remoteExpert.email, currentLive);
        }
      });
    }
  }

  public denyParticipant(participantId: string, live: Live) {
    void this.liveCommands.send(WebSocketActionEnum.DENIED, { email: participantId }, { destinationId: live?.code });
    if (this.removeFirstByEmail(this.toValidate, participantId)) {
      this.refreshParticipantList(live);
    }
  }

  public async inviteParticipant(participantId: string, live: Live): Promise<void> {
    if (this.removeFirstByEmail(this.toValidate, participantId)) {
      await this.inviteParticipants([participantId], live);
      void this.liveCommands.send(WebSocketActionEnum.APPROVED, { email: participantId }, { destinationId: live.code });
      this.refreshParticipantList(live);
    }
  }

  public async inviteParticipants(extenralIds: string[], live: Live): Promise<boolean> {
    return await lastValueFrom(
      this.inviteToLive({
        liveCode: live.code,
        invitedPeoples: extenralIds.map((i) => {
          return { email: i, role: RoleEnum.Guest } as InvitedPeopleDto;
        }),
      }),
    );
  }

  public inviteToLive(inviteToLive: InvitePeopleDto): Observable<boolean> {
    return this.httpClient.post<boolean>(`${environment.apiUrl}/Live/InviteToLive`, inviteToLive);
  }

  public updateParticipantsList(message: InboundWsMessage<WebSocketActionEnum.APPROVAL>) {
    const notificationTimer = 10;
    const currentAlert = this.toValidateNotification.get();
    const currentList = this.toValidate.get();
    const requestParticipant = {
      id: message.message.params.email,
      name: message.message.params.name,
    };

    if (
      currentAlert.findIndex((ca) => ca.email.toLowerCase() === requestParticipant.id.toLowerCase()) === -1 &&
      currentList.findIndex((cl) => cl.email.toLowerCase() === requestParticipant.id.toLowerCase()) === -1
    ) {
      const waitingParticipant = {
        email: requestParticipant.id,
        name: requestParticipant.name,
        shortName: getShortName(requestParticipant.name),
      } as ParticipantToValidate;

      this.toValidateNotification.set([...currentAlert, waitingParticipant]);
      this.toValidate.set([...currentList, waitingParticipant]);

      timer(notificationTimer * 1000)
        .pipe(
          switchMap(() => {
            this.dismissNotification(requestParticipant.id);
            return [];
          }),
        )
        .subscribe();
    }
  }

  public removeParticipantFromLists(message: InboundWsMessage<WebSocketActionEnum.APPROVED | WebSocketActionEnum.DENIED>) {
    const participantId = message.message.params.email;
    this.dismissNotification(participantId);
    this.removeFirstByEmail(this.toValidate, participantId);
  }

  public dismissNotification(participantId: string): void {
    this.removeFirstByEmail(this.toValidateNotification, participantId);
  }

  private removeFirstByEmail(state: SyncState<ParticipantToValidate[]>, email: string): boolean {
    const list = state.get();
    const index = list.findIndex((p) => p.email.toLowerCase() === email.toLowerCase());
    if (index === -1) {
      return false;
    }
    state.set(list.filter((_, i) => i !== index));
    return true;
  }

  public handleMuteMessage(message: InboundWsMessage<MuteAction>) {
    const participants = this.participants.get();
    // The live channel always carries the uuid: only the one-participant MUTE is bare.
    const participantIndex = participants.findIndex((p) => p.uuid.toLowerCase() === message.message.params!.uuid.toLowerCase());
    if (participantIndex === -1) {
      this.participants.set([...participants]);
      return;
    }
    const isMuted = [WebSocketActionEnum.MUTE, WebSocketActionEnum.MUTE_SELF].includes(message.message.action);
    this.participants.set(participants.map((p, i) => (i === participantIndex ? { ...p, isMuted } : p)));
  }

  public resetState() {
    this.participants.set([]);
    this.currentParticipant.set(null);
    this.toValidate.set([]);
    this.toValidateNotification.set([]);
  }
}
